<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'

import { useCases, utils } from 'frontend-js-shared'

import revMixin from './revMixin'

export default {
  mixins: [revMixin],

  computed: {
    ...mapGetters(['account', 'organizationSiteGroups']),
  },

  methods: {
    buildPermissionRev(rev) {
      const safeRev = rev || {}

      return {
        ownerId:
          utils.getEntityId(safeRev?.team?.owner) ??
          utils.getEntityId(safeRev?.owner) ??
          null,
        coachId:
          utils.getEntityId(safeRev?.team?.coach) ??
          utils.getEntityId(safeRev?.coach) ??
          null,
        collaboratorIds: _.filter(
          _.map(
            safeRev?.team?.collaborators ?? safeRev?.collaborators ?? [],
            collaborator => utils.getEntityId(collaborator)
          ),
          Boolean
        ),
        organizationId:
          utils.getEntityId(safeRev?.location?.organization) ??
          utils.getEntityId(safeRev?.organization) ??
          null,
        siteId:
          utils.getEntityId(safeRev?.location?.site) ??
          utils.getEntityId(safeRev?.site) ??
          null,
        type: safeRev?.type,
        status: this.getRevStatus(safeRev),
        approvedByMe: Boolean(safeRev?.approvedByMe),
        isImplemented: this.isRevValidated(safeRev),
        isFinanciallyValidated: this.isFinanciallyValidated(safeRev),
      }
    },

    getPermissions({
      rev,
      account = this.account,
      isTranslated = false,
      organizationSiteGroups = this.organizationSiteGroups ?? [],
    } = {}) {
      if (!account) {
        return this._defaultPermissions()
      }

      const permissionRev = this.buildPermissionRev(rev)
      const revPermissions = useCases.revs.getPermissions({
        currentAccount: account,
        rev: permissionRev,
        organizationSiteGroups,
      })

      const isRevCoach = utils.roles.isCoachOfRev(account, permissionRev)

      const isRevAdmin = utils.roles.hasAdminPermissions({
        account,
        siteId: permissionRev.siteId,
        organizationId: permissionRev.organizationId,
        organizationSiteGroups,
      })

      const isRevOwnerWithoutCoach = utils.roles.isOwnerWithoutCoachOfRev(
        account,
        permissionRev
      )

      const isRevSameSiteCoach = utils.roles.isSameSiteCoachOfRev(
        account,
        permissionRev
      )

      const isSameSiteCoachOrAdmin = utils.roles.isSameSiteCoachOrAdminOfRev(
        account,
        permissionRev
      )

      const isOwnerCoachOrAdmin = Boolean(
        rev?.team?.owner &&
        (rev.team.owner.isAdmin ||
          rev.team.owner.isOrganizationAdmin ||
          rev.team.owner.isSiteAdmin ||
          rev.team.owner.isCoach)
      )

      return Object.freeze({
        edit: !isTranslated && revPermissions.edit,
        roleCanComplete: revPermissions.edit,
        delete: revPermissions.delete,
        validate: revPermissions.validate,
        implement: revPermissions.implement,
        removeImplementation: revPermissions.removeImplementation,
        approve: revPermissions.approve,
        removeApproval: revPermissions.removeApproval,
        editStatus: !isTranslated && revPermissions.editStatus,
        canHighlight: revPermissions.highlight,
        complete: revPermissions.complete,
        isSameSiteCoachOrAdmin,
        isRevCoach,
        isRevAdmin,
        isRevOwnerWithoutCoach,
        isRevSameSiteCoach,
        isOwnerCoachOrAdmin,
      })
    },

    getCoachRequirement({
      rev,
      account = this.account,
      organizationSiteGroups = this.organizationSiteGroups ?? [],
    } = {}) {
      return useCases.revs.getCoachRequirement({
        currentAccount: account,
        rev: this.buildPermissionRev(rev),
        organizationSiteGroups,
      })
    },

    _defaultPermissions() {
      return Object.freeze({
        edit: false,
        roleCanComplete: false,
        delete: false,
        validate: false,
        implement: false,
        removeImplementation: false,
        approve: false,
        removeApproval: false,
        editStatus: false,
        canHighlight: false,
        complete: false,
        isSameSiteCoachOrAdmin: false,
        isRevCoach: false,
        isRevAdmin: false,
        isRevOwnerWithoutCoach: false,
        isRevSameSiteCoach: false,
        isOwnerCoachOrAdmin: false,
      })
    },
  },
}
</script>
